import * as React from "react";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { addDays, stripToDay } from "../../../../../../../react-core/utils/date";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { datePickerOptions, FrequencyTypeArrays, FrequencyArraysMap, processTypeEnum } from "../../Resource/Contents";
import PayeEstimate from "./PayeEstimate";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { RegularIncomePaymentAlertMsg } from "../../Resource/Contents";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { PaymentMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { RegularCrystallisationDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/RegularCrystallisationDetails";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { isIncomeExhausted } from "./Helper/IncomePaymentHelper";
import { IncomePaymentMethod, IncomePaymentMethodMap, IncomePaymentMethodOptions } from "./Resource/Content";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";

interface IRegularIncomePaymentProps extends IProcessComponentProps {
    data: DrawdownAnnualIncome;
    onChange: (changes) => void;
    labels: any;
    messages: any;
    drawdownType: DrawdownType;
    availableCash?: any;
    taxFreeLumpSum?: any;
    shouldHidePaymentMethod?: boolean;
    regularCrystallisationDetails?: RegularCrystallisationDetails;
    pensionBase: PensionBase;
    processType?: processTypeEnum;
    isITFC: boolean;
}
interface IRegularIncomePaymentState {
    firstPaymentDateAlertDisplay?: boolean;
    endDateDisplay: boolean;
}

export class RegularIncomePayment extends ProcessComponent<IRegularIncomePaymentProps, IRegularIncomePaymentState> {
    constructor(props: any) {
        super(props);
        this.state = {
            firstPaymentDateAlertDisplay: false,
            endDateDisplay: props.data?.PaymentEnd != null,
        };
    }

    componentDidMount() {
        const { onChange, drawdownType, regularCrystallisationDetails, availableCash, taxFreeLumpSum } = this.props;
        super.componentDidMount();

        if (drawdownType === DrawdownType.Regular && regularCrystallisationDetails) {
            onChange({
                Frequency: regularCrystallisationDetails.Frequency,
                PaymentMethod: PaymentMethod.Bacs,
                NextPayment: regularCrystallisationDetails.FirstPaymentDay,
            });
        } else {
            let daysUntilFirstPaymentDate = 10;
            if (availableCash?.Amount < taxFreeLumpSum?.Amount) {
                daysUntilFirstPaymentDate = 15;
            }
            onChange({
                Frequency: Frequency.Monthly,
                NextPayment: JsonDateParser.toJsonDateNoOffset(this.daysFromNow(daysUntilFirstPaymentDate)),
            });
        }
    }

    private daysFromNow(days): Date {
        const result = stripToDay(addDays(new Date(Date.now()), days));
        if (result.getDate() > 28) {
            result.setDate(28);
        }
        return result;
    }

    public render(): JSX.Element {
        const {
            data,
            labels,
            messages,
            drawdownType,
            availableCash,
            taxFreeLumpSum,
            shouldHidePaymentMethod,
            onChange,
            pensionBase,
            processType,
            isITFC,
        } = this.props;

        let paymentMethodOptions = IncomePaymentMethodOptions;
        let defaultPaymentMethod = IncomePaymentMethod.FasterOrChaps;
        if (taxFreeLumpSum > 0) {
            if (drawdownType === DrawdownType.Regular && data.GrossInitialIncomePayment.Amount <= 0) {
                paymentMethodOptions = [IncomePaymentMethod.Bacs];
                defaultPaymentMethod = IncomePaymentMethod.Bacs;
            }
        } else {
            paymentMethodOptions = [IncomePaymentMethod.Bacs];
        }

        let daysUntilFirstPaymentDate = 10;
        let firstPaymentDateMoreThanMessage = messages.firstPaymentDateUnder10;
        if (availableCash?.Amount < taxFreeLumpSum?.Amount) {
            daysUntilFirstPaymentDate = 15;
            firstPaymentDateMoreThanMessage = messages.firstPaymentDateUnder15;
        }

        return (
            <>
                <div className="row">
                    <div className="col-4">
                        <SelectEditor
                            label={labels.incomeFrequency}
                            options={FrequencyTypeArrays}
                            selected={data?.Frequency}
                            getText={(v) => {
                                return FrequencyArraysMap[v];
                            }}
                            required={drawdownType !== DrawdownType.Regular && messages.incomeFrequencyRequired}
                            disabled={drawdownType === DrawdownType.Regular}
                            onChange={(v) => {
                                onChange({
                                    Frequency: v,
                                });
                            }}
                        />
                    </div>
                </div>
                {drawdownType === DrawdownType.Regular && (
                    <Alert message={RegularIncomePaymentAlertMsg.Frenquency} alertType={AlertType.Warning} />
                )}
                {isIncomeExhausted(drawdownType, data, pensionBase, processType, isITFC) && (
                    <>
                        <div className="row">
                            <div className="col-12">
                                <Alert
                                    data-test="flexiArrangementsExhausted"
                                    message={messages.flexiArrangementsExhausted}
                                    alertType={AlertType.Warning}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-8 pb-20">{labels.flexiArrangementsExhausted}</div>
                            <div className="col-4">
                                <CheckboxEditor
                                    value={data?.FlexiArrangementsExhaustedConfirmed || false}
                                    onChange={(v) => {
                                        onChange({ FlexiArrangementsExhaustedConfirmed: v });
                                    }}
                                    cssWrapper="rounded-checkbox pl-40"
                                    validationRules={[
                                        {
                                            errorMessage:
                                                "Please confirm that your client is happy to proceed with their post-retirement arrangment value being exhausted within a year",
                                            validator: (v) => {
                                                return (
                                                    isIncomeExhausted(
                                                        drawdownType,
                                                        data,
                                                        pensionBase,
                                                        processType,
                                                        isITFC
                                                    ) && v
                                                );
                                            },
                                        },
                                    ]}
                                />
                            </div>
                        </div>
                    </>
                )}
                {!shouldHidePaymentMethod && (
                    <div className="row">
                        <div className="col-4">
                            <SelectEditor
                                label={labels.paymentMethod}
                                options={paymentMethodOptions}
                                selected={
                                    data?.PaymentMethod
                                        ? (IncomePaymentMethod[data?.PaymentMethod] as keyof typeof IncomePaymentMethod)
                                        : defaultPaymentMethod
                                }
                                getText={(v) => {
                                    return IncomePaymentMethodMap[v];
                                }}
                                onChange={(v) => onChange({ PaymentMethod: v })}
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-4">
                        <DatePicker
                            selected={JsonDateParser.toDateNoOffset(data?.NextPayment)}
                            label={labels.firstPaymentDate}
                            {...datePickerOptions}
                            alwaysValidateOnUpdate={true}
                            atLeast={{
                                errorMessage: firstPaymentDateMoreThanMessage,
                                value: this.daysFromNow(daysUntilFirstPaymentDate),
                            }}
                            // disabled={drawdownType === DrawdownType.Regular}
                            filterDate={{
                                errorMessage: messages.dateOver28,
                                validator: (v) => v.getDate() <= 28,
                            }}
                            required={messages.firstPaymentDateRequired}
                            onChange={(v) => {
                                onChange({ NextPayment: JsonDateParser.toJsonDateNoOffset(v) });
                                if (!this.state.firstPaymentDateAlertDisplay) {
                                    this.setState({
                                        firstPaymentDateAlertDisplay: true,
                                    });
                                }
                            }}
                        />
                    </div>
                </div>
                {drawdownType === DrawdownType.Regular && this.state.firstPaymentDateAlertDisplay && (
                    <Alert message={RegularIncomePaymentAlertMsg.FirstPaymentDate} alertType={AlertType.Warning} />
                )}
                {drawdownType !== DrawdownType.Regular && (
                    <>
                        <div className="row">
                            <div className="col-12">
                                <RadiosEditor
                                    inputGridClasses="w-400 pl-10"
                                    cssWrapper="rg-inline"
                                    label={labels.endDate}
                                    selected={!this.state.endDateDisplay}
                                    options={[true, false]}
                                    getText={(v) => (v ? labels.untilNotified : labels.enterADate)}
                                    onChange={(v) => {
                                        onChange({ PaymentEnd: null });
                                        this.setState({
                                            endDateDisplay: !v,
                                        });
                                    }}
                                />
                            </div>
                        </div>

                        <div className="row w-400">
                            <div className="col-6"></div>
                            <div className="col-6 pl-0">
                                <DatePicker
                                    data-test="endDateDate"
                                    selected={
                                        this.state.endDateDisplay
                                            ? JsonDateParser.toDateNoOffset(data?.PaymentEnd)
                                            : undefined
                                    }
                                    {...datePickerOptions}
                                    alwaysValidateOnUpdate={true}
                                    atLeast={
                                        this.state.endDateDisplay && {
                                            errorMessage: messages.endDateUnder,
                                            value: data?.NextPayment
                                                ? JsonDateParser.toDateNoOffset(data?.NextPayment)
                                                : new Date(),
                                        }
                                    }
                                    required={this.state.endDateDisplay && messages.enterADateRequired}
                                    onChange={(v) => {
                                        onChange({ PaymentEnd: JsonDateParser.toJsonDateNoOffset(v) });
                                    }}
                                    disabled={!this.state.endDateDisplay}
                                />
                            </div>
                        </div>
                    </>
                )}
                {drawdownType !== DrawdownType.Regular && this.state.endDateDisplay && data?.PaymentEnd && (
                    <Alert message={RegularIncomePaymentAlertMsg.EndDate} alertType={AlertType.Warning} />
                )}
                <PayeEstimate data={null} onClick={() => {}} labels={labels} messages={messages} />
                <hr />
            </>
        );
    }
}

export default RegularIncomePayment;
